import {
  Box,
  Button,
  Checkbox,
  ColumnLayout,
  FormField,
  Icon,
  Input,
  Modal,
  Select,
  SpaceBetween,
  Textarea,
  Toggle,
} from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import React, { useEffect, useState } from 'react';
import {
  DATA_CLASSIFICATION_LEVELS_URL,
  dataClassificationOptions,
  defaultVisibility,
  PII_DEFINITION_URL,
  TABLE_STATUS_DEPRECATED,
  tableStatusOptions,
  TEMPLATE_TYPE_ACCESS_MANAGEMENT,
  TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW,
  visibilitySelection,
} from 'src/commons/constants';
import { editDataset } from 'src/api/catalog';
import { listTemplates } from 'src/api/permissions';
import { EditDataSetRequest, GetDataSetResponseStructure } from 'aws-sdk/clients/awsdlhybridcatalogservicelambda';
import { OptionDefinition } from '@amzn/awsui-components-react-v3/polaris/internal/components/option/interfaces';
import {
  fetchActiveConsumerDataPermissions,
  RESOURCE_TYPE_TABLE,
} from 'src/components/permissions/myBaselining/baseliningUtils';
import { getDatasetUpperLevelResourceId } from 'src/components/utils/hybridCatalog/idUtil';
import { findOption } from 'src/components/catalog/common';
import { DataCatalogResource } from 'src/components/catalog/datasetdetail/datasetDetailHelpers';
import { dpTypeForDataSource, getIdInfoFromId, IdInfo } from 'src/components/utils/arnUtil';

export interface DatasetEditModalProps {
  setContentType: any;
  activeGroup: string;
  username: string;
  activeWorkspace: any;
  dataset: GetDataSetResponseStructure;
  visible: boolean;
  dismiss: any;
  notifyEditSuccess: any;
  notifyEditFailure: any;
  setEditLoading: any;
  refreshPage: any;
}

export const DatasetEditModal = (props: DatasetEditModalProps) => {
  const [tableStatusOption, setTableStatusOption] = useState(undefined);
  const [datasetName, setDatasetName] = useState(undefined);
  const [datasetDesc, setDatasetDesc] = useState(undefined);
  const [isPii, setIsPii] = useState(props.dataset.PII ?? false);
  const [confidenceFile, setConfidenceFile] = useState(undefined);
  const [dataClassification, setDataClassification] = useState<OptionDefinition>(undefined);
  const [visibility, setVisibility] = useState<OptionDefinition>(defaultVisibility);
  const [deprecationConfirmed, setDeprecationConfirmed] = useState(undefined);
  const [deprecationConfirmationContent, setDeprecationConfirmationContent] = useState(undefined);

  const handleEdit = async () => {
    props.setEditLoading(true);
    try {
      props.dismiss();
      if (!(await validatePrerequisites())) {
        props.notifyEditFailure(
          'Unable to edit dataset. PII datasets must be associated with an access template, associate a template to it and try again.',
        );
        return;
      }
      let index = getTableStatusIndex(props.dataset.TableState);
      setTableStatusOption(tableStatusOptions[index]);
      // For legacy reasons the "Private" visibility is used for auth in the data classification field,
      // this means that we cannot set a classification and set the dataset as private until we modify all the places
      // where we check for "Private" in the data classification field.
      const classification = visibility?.value == 'Private' ? 'Private' : dataClassification.value;
      let request: EditDataSetRequest = {
        Id: props.dataset.Id,
        TableState: tableStatusOption.label,
        DataSetName: datasetName,
        Description: datasetDesc,
        DataClassification: classification,
        Visibility: visibility.value,
        PII: isPii,
        WheeljackConfidenceFileLocation: confidenceFile,
      };
      await editDataset(request);
      props.notifyEditSuccess('Successfully edited the dataset.');
    } catch (err) {
      props.notifyEditFailure('There was an error while editing the dataset: ' + err.message);
    } finally {
      props.setEditLoading(false);
      props.refreshPage();
    }
  };

  const validatePrerequisites = async () => {
    if (!isPii) {
      return true;
    }
    // For PII datasets, there must be at least one template associated with it, either AMI or ARW type.
    // ListTemplates API requires non-null TemplateType parameter, so we make two separate calls:
    const [amiTemplatesResult, arwTemplatesResult] = await Promise.all([
      // ListTemplates API is not paginated; we should make this change in Gladstone eventually.
      listTemplates({
        ownerId: props.dataset.PrimaryOwner,
        templateType: TEMPLATE_TYPE_ACCESS_MANAGEMENT,
        resourceId: props.dataset.Id,
      }),
      listTemplates({
        ownerId: props.dataset.PrimaryOwner,
        templateType: TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW,
        resourceId: props.dataset.Id,
      }),
    ]);
    return amiTemplatesResult?.templates?.length > 0 || arwTemplatesResult?.templates?.length > 0;
  };

  const handleRefresh = async () => {
    let tableStatusOptionIndex = getTableStatusIndex(props.dataset.TableState);
    setTableStatusOption(tableStatusOptions[tableStatusOptionIndex]);
    setDatasetName(props.dataset.DataSetName);
    setDatasetDesc(props.dataset.DataSetDesc);
    setConfidenceFile(props.dataset.WheeljackConfidenceFileLocation);
    setDeprecationConfirmed(undefined);
    setDeprecationConfirmationContent(undefined);
    setDataClassification({
      value: props.dataset.DataClassification,
      label: props.dataset.DataClassification,
    });
    if (props.dataset.Visibility) {
      setVisibility(findOption(visibilitySelection, props.dataset.Visibility));
    }
  };

  useEffect(() => {
    handleRefresh();
  }, []);

  const getTableStatusIndex = (tableState) => {
    for (const item of tableStatusOptions) {
      if (item.label === tableState) {
        return parseInt(item.id) - 1;
      }
    }
    return tableStatusOptions.length - 1;
  };

  const fetchActiveConsumerData = async (dataset) => {
    let dataSourceId = dataset.IdInfo.DataSourceId;
    const idInfo: IdInfo = getIdInfoFromId(dataset.Id);
    let resource: DataCatalogResource = {
      idInfo,
      accountId: dataset.IdInfo.CatalogId,
      region: dataset.IdInfo.Region,
      type: RESOURCE_TYPE_TABLE,
      dataCatalogObjectDetails: {
        dataSourceId: dataSourceId,
        clusterIdentifier: dataset.IdInfo.ClusterIdentifier,
        databaseName: dataset.IdInfo.DatabaseName,
        schemaName: dataset.IdInfo.SchemaName,
        tableName: dataset.IdInfo.TableName,
      },
      dpType: dpTypeForDataSource(dataSourceId),
      tagResourceId: dataset.Id,
      tagUpperLevelResourceId: getDatasetUpperLevelResourceId(dataset),
    };
    let activeDataPermissions = await fetchActiveConsumerDataPermissions(resource);
    setDeprecationConfirmationContent(
      `This dataset has ${activeDataPermissions.length} active consumer(s). Confirm deprecation? (Existing consumer permissions will not be revoked)`,
    );
  };

  useEffect(() => {
    if (tableStatusOption?.label == TABLE_STATUS_DEPRECATED && props.dataset != null) {
      fetchActiveConsumerData(props.dataset);
    }
  }, [tableStatusOption]);

  return (
    <Modal
      onDismiss={props.dismiss}
      visible={props.visible}
      closeAriaLabel='Close modal'
      footer={
        <Box float='right'>
          <SpaceBetween direction='horizontal' size='xs'>
            <Button variant='link' onClick={props.dismiss}>
              Cancel
            </Button>
            <Button
              variant='primary'
              onClick={handleEdit}
              disabled={tableStatusOption?.label == TABLE_STATUS_DEPRECATED && !deprecationConfirmed}
            >
              Submit
            </Button>
          </SpaceBetween>
        </Box>
      }
      size='medium'
      header='Edit dataset'
    >
      <ColumnLayout>
        <FormField label='Dataset ID' description='Dataset ID cannot be updated'>
          <Input placeholder={'Dataset ID'} value={props.dataset.Id} disabled />
        </FormField>
        <FormField label='Dataset name' description='Input the new name of the dataset'>
          <Input
            placeholder={'Dataset name'}
            value={datasetName}
            onChange={(e) => {
              setDatasetName(e.detail.value);
            }}
          />
        </FormField>
        <FormField label='Dataset description' description='Input the new description of the dataset'>
          <Input
            placeholder={'Dataset description'}
            value={datasetDesc}
            onChange={(e) => {
              setDatasetDesc(e.detail.value);
            }}
          />
        </FormField>
        <FormField label='Dataset status' description='Current status of the dataset.'>
          <Select
            selectedOption={tableStatusOption}
            options={tableStatusOptions}
            selectedAriaLabel='Selected'
            onChange={(e) => setTableStatusOption(e.detail.selectedOption)}
          />
        </FormField>

        <FormField
          label='Data classification level'
          description='Data classification level as defined by the AWS Data Handling Standard'
          info={
            <Link external={true} href={DATA_CLASSIFICATION_LEVELS_URL}>
              info
            </Link>
          }
        >
          <Select
            placeholder='Public'
            selectedOption={dataClassification}
            options={dataClassificationOptions}
            ariaRequired={true}
            onChange={({ detail }) => {
              setDataClassification(detail.selectedOption);
            }}
            disabled={false}
          />
        </FormField>

        <FormField label='Data visibility' description='Data visibility defines how your dataset are discoverable'>
          <Select
            placeholder='Public'
            selectedOption={visibility}
            options={visibilitySelection}
            ariaRequired={true}
            onChange={({ detail }) => {
              setVisibility(detail.selectedOption);
            }}
            disabled={false}
          />
        </FormField>

        <FormField label='Confidence files' description='Input the confidence files seperated by `,`'>
          <Textarea
            placeholder={'s3://file1, s3://file2'}
            value={confidenceFile}
            onChange={(e) => {
              setConfidenceFile(e.detail.value);
            }}
          />
        </FormField>
        <FormField
          label='Contains PII data'
          info={
            <Link external={true} href={PII_DEFINITION_URL}>
              info
            </Link>
          }
        >
          <Toggle checked={isPii} onChange={(e) => setIsPii(e.detail.checked)} />
        </FormField>

        {tableStatusOption?.label == TABLE_STATUS_DEPRECATED && deprecationConfirmationContent && (
          <Checkbox checked={deprecationConfirmed} onChange={(e) => setDeprecationConfirmed(e.detail.checked)}>
            <Icon name='status-warning' variant='warning' /> {deprecationConfirmationContent}
          </Checkbox>
        )}
      </ColumnLayout>
    </Modal>
  );
};
